import { ActivityEntityName } from '../../../../../entities/shared/activity.entity.name';
import { canDeleteItemInStatus, canEditItemInStatus } from '../../../../../entities/shared/item.status.rules';
import { ItemAuthor, resolveContractorCompanyId } from '../../../../../entities/shared/item.creator';
import { Party } from '../../../../../entities/entity.status/enum';
import { resolveCodeCapability } from '../../../../../entities/shared/code';
import { EntitySecurityCapabilityRegistry } from '../../../../entity.security.capability.registry';
import { EntitySecurityFacts } from '../../../../entity.security.facts';
import { JobSecurityRight } from '../../../../modules/job.security.module';
import { jobSecurityHasRight } from '../../job.security.has.right';
import { canWriteItemLine } from '../../section/job.section.policy';
import { stageIsClosedOrCancelled, stageIsFinalised } from '../stage.security.state';
import { StageItemSecurityCapabilities } from './stage.item.security.capabilities';
import { StageItemSecurityEntity } from './stage.item.security.entity';

EntitySecurityCapabilityRegistry.register<StageItemSecurityEntity, StageItemSecurityCapabilities>(ActivityEntityName.Item, getStageItemSecurityCapabilities);
EntitySecurityCapabilityRegistry.register<StageItemSecurityEntity, StageItemSecurityCapabilities>(ActivityEntityName.StageItem, getStageItemSecurityCapabilities);

export function getStageItemSecurityCapabilities(facts: EntitySecurityFacts<StageItemSecurityEntity, ItemAuthor>): StageItemSecurityCapabilities {

    const entity = facts.before ?? facts.entity ?? facts.after;
    const stage = entity?.stage;
    const item = entity?.item;
    const role = facts.role;
    const canManage = jobSecurityHasRight(role, JobSecurityRight.Manage);
    const canModifyFinalised = jobSecurityHasRight(role, JobSecurityRight.ModifyJobAfterFinalise);
    // Closed/Cancelled stages are terminal — items cannot be added, edited, or deleted (matches the
    // stage capability and the UI, which makes the whole stage read-only with no override).
    const stageAllowsManage = canManage && !stageIsClosedOrCancelled(stage) && (!stageIsFinalised(stage) || canModifyFinalised);
    const statusAllowsEdit = resolveStatusEdit(entity, facts.user);
    const canUpdate = stageAllowsManage && statusAllowsEdit && (!item || resolveCodeCapability(item, 'edit'));
    // Cancel answers to the target status's `selectableBy`, never to the current status's `editableBy` —
    // that separation is what lets a line in a locked status still be cancelled.
    const canCancel = stageAllowsManage && (!item || resolveCodeCapability(item, 'edit'));
    const lineLeavable = stageAllowsManage && !item?.purchaseOrder?._id;
    // The client lock and a claim import refuse a removal, not a move: a move re-homes the same line on the same job,
    // carrying the lock and the upstream's integrationId with it (WBS-12702, WBS-13373).
    const lineRemovable = lineLeavable && !item?.claimImport && (!item || resolveCodeCapability(item, 'delete'));
    // Delete needs the section's Delete right; a move re-homes the same line, so it needs the section's Edit.
    const canDelete = lineRemovable && resolveStatusDelete(entity, facts.user) && (!item || canWriteItemLine(role, item, 'delete'));
    // A pending line's note is one of the details its status locks; an authorised line keeps the note open in every status.
    const statusLocksNote = entity?.status?.entityType === ActivityEntityName.ItemPending && !statusAllowsEdit;

    return {
        canCreate: stageAllowsManage,
        canUpdate,
        canUpdateNote: stageAllowsManage && !statusLocksNote,
        canDelete,
        canCancel,
        // An invoiced or billed line stays with the stage its document was raised against (WBS-12326).
        canMove: lineLeavable && statusAllowsEdit && !item?.invoice?._id && !item?.bill?._id && (!item || canWriteItemLine(role, item, 'edit')),
        // Approving a requested line commits money, so it needs its own right (SP-8, EV-51). Decline
        // keeps the existing job rights.
        canApprove: stageAllowsManage && jobSecurityHasRight(role, JobSecurityRight.ApprovePendingItems),
        canViewPricing: jobSecurityHasRight(role, JobSecurityRight.ViewPricing, JobSecurityRight.ModifyPricing),
        canModifyPricing: stageAllowsManage && jobSecurityHasRight(role, JobSecurityRight.ModifyPricing),
        canModifyLockedItems: jobSecurityHasRight(role, JobSecurityRight.ModifyLockedItems),
        canManageMedia: stageAllowsManage && (!item || canWriteItemLine(role, item, 'edit')),
        canEditSection: !item || canWriteItemLine(role, item, 'edit'),
    };
}

function resolveStatusEdit(entity: StageItemSecurityEntity | undefined, user: ItemAuthor | undefined): boolean {

    return canEditItemInStatus(entity?.status, entity?.party, entity?.item?.raisedBy, resolveCompanyId(entity?.party, user, entity?.actingCompanyId));
}

function resolveStatusDelete(entity: StageItemSecurityEntity | undefined, user: ItemAuthor | undefined): boolean {

    return canDeleteItemInStatus(entity?.status, entity?.party, entity?.item?.raisedBy, resolveCompanyId(entity?.party, user, entity?.actingCompanyId));
}

/** Only a contractor's company narrows an author window, and only a caller with an identity has one. */
function resolveCompanyId(party: Party | undefined, user: ItemAuthor | undefined, actingCompanyId?: string): string | undefined {

    return party === Party.Contractor && user?._id ? resolveContractorCompanyId(user, actingCompanyId) : undefined;
}
